
interface PerformanceData {
  timestamp: number;
  metric: string;
  value: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitor {
  private metrics: PerformanceData[] = [];
  private observers: PerformanceObserver[] = [];
  private isMonitoring = false;

  start() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    console.log('[PerformanceMonitor] Starting performance monitoring');

    this.setupObservers();
    this.monitorMemoryUsage();
    this.monitorRenderPerformance();
  }

  stop() {
    this.isMonitoring = false;
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    console.log('[PerformanceMonitor] Stopped performance monitoring');
  }

  private setupObservers() {
    if (!('PerformanceObserver' in window)) {
      console.warn('[PerformanceMonitor] PerformanceObserver not supported');
      return;
    }

    // Long Task Observer
    try {
      const longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.recordMetric('long_task', entry.duration, {
            startTime: entry.startTime,
            name: entry.name
          });
          
          if (entry.duration > 100) {
            console.warn(`[PerformanceMonitor] Long task detected: ${entry.duration}ms`);
          }
        }
      });
      
      longTaskObserver.observe({ entryTypes: ['longtask'] });
      this.observers.push(longTaskObserver);
    } catch (error) {
      console.warn('[PerformanceMonitor] Long task observer not supported');
    }

    // Layout Shift Observer
    try {
      const layoutShiftObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // Safely cast and check for value property
          const layoutShiftEntry = entry as any;
          if ('value' in layoutShiftEntry && typeof layoutShiftEntry.value === 'number') {
            this.recordMetric('layout_shift', layoutShiftEntry.value, {
              startTime: entry.startTime,
              hadRecentInput: layoutShiftEntry.hadRecentInput
            });
          }
        }
      });
      
      layoutShiftObserver.observe({ entryTypes: ['layout-shift'] });
      this.observers.push(layoutShiftObserver);
    } catch (error) {
      console.warn('[PerformanceMonitor] Layout shift observer not supported');
    }

    // Paint Observer
    try {
      const paintObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.recordMetric(`paint_${entry.name.replace('-', '_')}`, entry.startTime);
        }
      });
      
      paintObserver.observe({ entryTypes: ['paint'] });
      this.observers.push(paintObserver);
    } catch (error) {
      console.warn('[PerformanceMonitor] Paint observer not supported');
    }
  }

  private monitorMemoryUsage() {
    if (!('memory' in performance)) {
      return; // Silently skip if not supported
    }

    const checkMemory = () => {
      if (!this.isMonitoring) return;

      const memory = (performance as any).memory;
      if (memory) {
        // Only warn if memory usage is critically high
        const usagePercentage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
        if (usagePercentage > 90) {
          console.warn(`[PerformanceMonitor] Critical memory usage: ${usagePercentage.toFixed(1)}%`);
          this.recordMetric('memory_used', memory.usedJSHeapSize);
        }
      }

      setTimeout(checkMemory, 30000); // Check every 30 seconds instead of 10
    };

    checkMemory();
  }

  private monitorRenderPerformance() {
    let lastFrameTime = performance.now();
    let frameCount = 0;
    let lowFpsWarningCount = 0;

    const measureFPS = () => {
      if (!this.isMonitoring) return;

      const currentTime = performance.now();
      const deltaTime = currentTime - lastFrameTime;
      
      if (deltaTime >= 10000) { // Calculate FPS every 10 seconds for reduced noise
        const fps = Math.round((frameCount * 1000) / deltaTime);
        
        // Only warn about critically low FPS (< 10) and limit warnings to 3 total
        if (fps < 10 && lowFpsWarningCount < 3) {
          console.warn(`[PerformanceMonitor] Critical FPS: ${fps}`);
          this.recordMetric('fps', fps);
          lowFpsWarningCount++;
        }
        
        frameCount = 0;
        lastFrameTime = currentTime;
      } else {
        frameCount++;
      }

      requestAnimationFrame(measureFPS);
    };

    measureFPS();
  }

  private recordMetric(metric: string, value: number, metadata?: Record<string, any>) {
    const data: PerformanceData = {
      timestamp: Date.now(),
      metric,
      value,
      metadata
    };

    this.metrics.push(data);

    // Keep only last 1000 metrics to prevent memory leaks
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }

    // Log critical performance issues
    if (this.isCriticalMetric(metric, value)) {
      console.warn(`[PerformanceMonitor] Critical metric: ${metric} = ${value}`, metadata);
    }
  }

  private isCriticalMetric(metric: string, value: number): boolean {
    const criticalThresholds: Record<string, number> = {
      'long_task': 100,
      'layout_shift': 0.1,
      'fps': 30
    };

    const threshold = criticalThresholds[metric];
    if (!threshold) return false;

    return metric === 'fps' ? value < threshold : value > threshold;
  }

  getMetrics(): PerformanceData[] {
    return [...this.metrics];
  }

  getMetricSummary(): Record<string, { avg: number; max: number; min: number; count: number }> {
    const summary: Record<string, { avg: number; max: number; min: number; count: number }> = {};

    this.metrics.forEach(({ metric, value }) => {
      if (!summary[metric]) {
        summary[metric] = { avg: 0, max: value, min: value, count: 0 };
      }

      const current = summary[metric];
      current.max = Math.max(current.max, value);
      current.min = Math.min(current.min, value);
      current.count++;
      current.avg = (current.avg * (current.count - 1) + value) / current.count;
    });

    return summary;
  }

  clearMetrics() {
    this.metrics = [];
    console.log('[PerformanceMonitor] Metrics cleared');
  }
}

export const performanceMonitor = new PerformanceMonitor();
